import { useState, useEffect, useRef, useMemo } from "react";
import {
  startOfWeek,
  addDays,
  isSameDay,
  parseISO,
  format,
  isToday,
  setHours,
  setMinutes,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Video, Plus, Trash2, FileText } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import type { Task } from "../types";
import {
  syncTaskWithGoogleCalendar,
  deleteEventFromGoogleCalendar,
  getGCalHtmlLink,
  getGCalEventId,
} from "../providers/google/googleIntegrationService";
import { cn } from "@/lib/utils";

interface AgendaWeekViewProps {
  currentDate: Date;
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onSelectDate: (date: Date) => void;
  onRefresh: () => void;
  salesList?: Array<{ id: any; first_name: string; last_name?: string }>;
}

// Helper para formatar horários no estilo Google Calendar (ex: 8:30 – 9:15am, 12 – 1pm, 8am – 6pm)
function formatGoogleCalendarTime(start: Date, end: Date): string {
  const formatPart = (d: Date) => {
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? "pm" : "am";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return {
      h12,
      m,
      ampm,
      strWithAmpm:
        m === 0
          ? `${h12}${ampm}`
          : `${h12}:${String(m).padStart(2, "0")}${ampm}`,
      strNoAmpm: m === 0 ? `${h12}` : `${h12}:${String(m).padStart(2, "0")}`,
    };
  };

  const s = formatPart(start);
  const e = formatPart(end);

  if (s.ampm === e.ampm) {
    return `${s.strNoAmpm} – ${e.strWithAmpm}`;
  }
  return `${s.strWithAmpm} – ${e.strWithAmpm}`;
}

// Configurações do Grid estilo Google Calendar
const HOUR_HEIGHT = 64; // 64px por hora
const MINUTE_HEIGHT = HOUR_HEIGHT / 60; // ~1.0667px por minuto
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Helper para cor uniforme e profissional de todas as atividades (sem cores variadas/coloridas)
function getEventColor(_task?: Task) {
  return {
    bg: "bg-[#2563eb] dark:bg-[#1d4ed8]",
    border: "border-[#1e40af] dark:border-[#3b82f6]/40",
    text: "text-white",
    hover: "hover:bg-[#1d4ed8] dark:hover:bg-[#2563eb]",
  };
}

export const AgendaWeekView = ({
  currentDate,
  tasks,
  onEditTask,
  onSelectDate,
  onRefresh,
  salesList,
}: AgendaWeekViewProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [dragOverCell, setDragOverCell] = useState<{
    dayIso: string;
    hour: number;
  } | null>(null);

  const salesMap = useMemo(() => {
    const map = new Map<string, string>();
    salesList?.forEach((s) => {
      map.set(String(s.id), s.first_name);
    });
    return map;
  }, [salesList]);

  // Mantém a hora atual atualizada a cada minuto
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Semana completa com 7 dias (Segunda a Domingo)
  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate],
  );
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Auto-scroll inicial para a hora atual (ou 8:00 se muito cedo)
  useEffect(() => {
    if (scrollContainerRef.current) {
      const now = new Date();
      const targetHour = Math.max(7, Math.min(now.getHours() - 1, 19));
      scrollContainerRef.current.scrollTop = targetHour * HOUR_HEIGHT;
    }
  }, []);

  // Deduplica tarefas idênticas (mesmo título normalizado e horário de início) para evitar cartões clones na grade
  const deduplicatedTasks = useMemo(() => {
    const seen = new Map<string, Task>();
    for (const t of tasks) {
      const startStr = t.data_inicio || t.due_date || "";
      const gcalId = t.google_calendar_event_id || getGCalEventId(t.id);
      const normTitle = (t.text || "")
        .toLowerCase()
        .replace(/^(reunião:\s*)+/i, "")
        .replace(/[^a-z0-9]/gi, "")
        .trim();
      const timeSlot = startStr.slice(0, 16);

      // Chave preferencial: se tem gcalId usa, mas se já existe outra tarefa no mesmo minuto com título igual, agrupa
      const key = gcalId
        ? `gcal_${gcalId}`
        : normTitle
          ? `${normTitle}_${timeSlot}`
          : `task_${t.id}`;

      // Chave secundária para colapsar clones gerados por sincronização do mesmo compromisso
      const timeTitleKey = normTitle ? `time_${normTitle}_${timeSlot}` : null;

      if (timeTitleKey && seen.has(timeTitleKey)) {
        const existing = seen.get(timeTitleKey)!;
        // Prioriza a tarefa que tem link do Google Calendar ou do Meet
        if (!existing.link_chamada && t.link_chamada) {
          seen.set(timeTitleKey, t);
        } else if (
          !existing.google_calendar_event_id &&
          t.google_calendar_event_id
        ) {
          seen.set(timeTitleKey, t);
        }
        continue;
      }

      if (!seen.has(key)) {
        seen.set(key, t);
        if (timeTitleKey) seen.set(timeTitleKey, t);
      } else {
        const existing = seen.get(key)!;
        if (!existing.google_calendar_event_id && t.google_calendar_event_id) {
          seen.set(key, t);
        } else if (!existing.link_chamada && t.link_chamada) {
          seen.set(key, t);
        }
      }
    }
    // Retorna valores únicos
    return Array.from(new Set(seen.values()));
  }, [tasks]);

  // Separação e layout inteligente de sobreposição estilo Google Calendar
  const daysLayoutData = useMemo(() => {
    return weekDays.map((day) => {
      const dayTasks = deduplicatedTasks.filter((task) => {
        const taskDateStr = task.data_inicio || task.due_date;
        if (!taskDateStr) return false;
        try {
          return isSameDay(parseISO(taskDateStr), day);
        } catch {
          return false;
        }
      });

      // Tarefas com horário definido
      const timed = dayTasks.filter((t) => {
        const str = t.data_inicio || t.due_date;
        return str && !str.includes("T00:00:00");
      });

      // Tarefas de dia inteiro ou sem horário
      const allDay = dayTasks.filter((t) => !timed.includes(t));

      // Algoritmo de posicionamento e colunas para sobreposições (Interval Graph Coloring)
      const parsedTimed = timed.map((task) => {
        const start = parseISO(task.data_inicio || task.due_date!);
        const startMinutes = start.getHours() * 60 + start.getMinutes();

        let endMinutes = startMinutes + 45; // padrão 45 min
        let end = new Date(start.getTime() + 45 * 60000);
        if (task.data_fim) {
          try {
            end = parseISO(task.data_fim);
            const diff = end.getHours() * 60 + end.getMinutes() - startMinutes;
            if (diff > 0) endMinutes = startMinutes + diff;
          } catch {}
        }

        const durationMinutes = Math.max(20, endMinutes - startMinutes);
        const top = startMinutes * MINUTE_HEIGHT;
        const height = Math.max(24, durationMinutes * MINUTE_HEIGHT);

        return {
          task,
          start,
          end,
          startMinutes,
          endMinutes,
          durationMinutes,
          top,
          height,
        };
      });

      // Ordena por horário de início e depois pela maior duração
      parsedTimed.sort(
        (a, b) =>
          a.startMinutes - b.startMinutes ||
          b.durationMinutes - a.durationMinutes,
      );

      // Agrupamento em clusters de sobreposição
      const clusters: (typeof parsedTimed)[] = [];
      let currentCluster: typeof parsedTimed = [];
      let clusterEnd = -1;

      for (const event of parsedTimed) {
        if (currentCluster.length === 0) {
          currentCluster.push(event);
          clusterEnd = event.endMinutes;
        } else if (event.startMinutes < clusterEnd) {
          currentCluster.push(event);
          clusterEnd = Math.max(clusterEnd, event.endMinutes);
        } else {
          clusters.push(currentCluster);
          currentCluster = [event];
          clusterEnd = event.endMinutes;
        }
      }
      if (currentCluster.length > 0) {
        clusters.push(currentCluster);
      }

      // Distribuição gulosa de colunas para cada cluster
      const timedEvents: ((typeof parsedTimed)[0] & {
        column: number;
        totalColumns: number;
      })[] = [];

      for (const cluster of clusters) {
        const columns: (typeof parsedTimed)[] = [];

        const assigned = cluster.map((event) => {
          let col = 0;
          while (true) {
            if (!columns[col]) {
              columns[col] = [event];
              break;
            }
            const collision = columns[col].some(
              (other) =>
                event.startMinutes < other.endMinutes &&
                event.endMinutes > other.startMinutes,
            );
            if (!collision) {
              columns[col].push(event);
              break;
            }
            col++;
          }
          return { ...event, column: col, totalColumns: 1 };
        });

        // Calcula totalColumns para cada evento baseado no número máximo de eventos simultâneos
        for (const item of assigned) {
          const concurrent = assigned.filter(
            (other) =>
              item.startMinutes < other.endMinutes &&
              item.endMinutes > other.startMinutes,
          );
          const maxCol = Math.max(...concurrent.map((c) => c.column));
          item.totalColumns = maxCol + 1;
        }

        timedEvents.push(...assigned);
      }

      return {
        day,
        isDayToday: isToday(day),
        allDay,
        timedEvents,
      };
    });
  }, [weekDays, deduplicatedTasks]);

  // Handler para mover a atividade arrastando para um dia e hora específicos
  const handleDropOnTimeSlot = async (
    targetDay: Date,
    targetHour: number,
    targetMinute: number,
    e: React.DragEvent,
  ) => {
    e.preventDefault();
    setDragOverCell(null);

    const rawData = e.dataTransfer.getData("application/json");
    let taskId: string | number | null = null;
    let durationMinutes = 45;

    if (rawData) {
      try {
        const parsed = JSON.parse(rawData);
        taskId = parsed.taskId;
        if (parsed.durationMinutes) durationMinutes = parsed.durationMinutes;
      } catch {}
    }
    if (!taskId) taskId = e.dataTransfer.getData("text/plain");
    if (!taskId) return;

    const task = tasks.find((t) => String(t.id) === String(taskId));
    if (!task) return;

    const newStart = setMinutes(
      setHours(new Date(targetDay), targetHour),
      targetMinute,
    );
    newStart.setSeconds(0, 0);

    const newEnd = new Date(newStart.getTime() + durationMinutes * 60 * 1000);

    try {
      await dataProvider.update("tasks", {
        id: task.id,
        data: {
          ...task,
          data_inicio: newStart.toISOString(),
          due_date: newStart.toISOString(),
          data_fim: newEnd.toISOString(),
        },
        previousData: task,
      });

      // Sincroniza imediatamente com o Google Agenda
      syncTaskWithGoogleCalendar({
        id: task.id,
        text: task.text,
        type: task.type,
        data_inicio: newStart.toISOString(),
        due_date: newStart.toISOString(),
        data_fim: newEnd.toISOString(),
        link_chamada: task.link_chamada,
      }).catch(() => {});

      notify(
        `Atividade movida para ${format(newStart, "EEEE, d 'de' MMMM", { locale: ptBR })} às ${format(newStart, "HH:mm")}`,
        { type: "success" },
      );
      onRefresh();
    } catch {
      notify("Erro ao mover atividade", { type: "error" });
    }
  };

  // Clique em slot vazio para criar atividade pré-preenchida naquele horário
  const handleSlotClick = (day: Date, hour: number, minute: number = 0) => {
    const slotDate = setMinutes(setHours(new Date(day), hour), minute);
    onSelectDate(slotDate);
  };

  // Helper para formatar a hora no gutter esquerdo (estilo Google Calendar: 8 AM, 12 PM, 1 PM)
  const formatGutterHour = (h: number) => {
    if (h === 0) return "12 AM";
    if (h === 12) return "12 PM";
    return h > 12 ? `${h - 12} PM` : `${h} AM`;
  };

  return (
    <div className="flex flex-col rounded-xl border border-border/80 bg-background overflow-hidden shadow-sm select-none">
      {/* 1. CABEÇALHO DOS DIAS DA SEMANA (ESTILO GOOGLE CALENDAR) */}
      <div className="flex border-b border-border/60 bg-muted/20 sticky top-0 z-30">
        {/* Espaço para a coluna de horários à esquerda */}
        <div className="w-16 shrink-0 border-r border-border/60 p-2 text-right">
          <span className="text-[10px] font-mono text-muted-foreground uppercase">
            GMT-3
          </span>
        </div>

        {/* Colunas dos 7 Dias da Semana */}
        <div className="flex-1 grid grid-cols-7 divide-x divide-border/60">
          {daysLayoutData.map(({ day, isDayToday, allDay }) => (
            <div
              key={day.toISOString()}
              className={cn(
                "p-2 text-center transition-colors relative flex flex-col items-center justify-center min-h-[58px]",
                isDayToday && "bg-primary/[0.04]",
              )}
            >
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {format(day, "EEE", { locale: ptBR })}
              </div>

              <div
                className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold mt-0.5 transition-all cursor-pointer hover:bg-muted/80",
                  isDayToday
                    ? "bg-primary text-primary-foreground shadow-xs"
                    : "text-foreground",
                )}
                onClick={() => onSelectDate(day)}
                title="Novo compromisso neste dia"
              >
                {format(day, "d")}
              </div>

              {/* Eventos do dia inteiro ou sem horário fixo */}
              {allDay.length > 0 && (
                <div className="w-full mt-1.5 space-y-1">
                  {allDay.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => onEditTask(t)}
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary truncate cursor-pointer hover:opacity-85 text-left border border-primary/30 flex items-center justify-between gap-1"
                      title={t.text}
                    >
                      <span className="truncate">{t.text}</span>
                      {t.sales_id && salesMap.has(String(t.sales_id)) && (
                        <span className="text-[9px] opacity-75 shrink-0">
                          {salesMap.get(String(t.sales_id))}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 2. GRADE VERTICAL DE HORÁRIOS CONTÍNUA (ESTILO GOOGLE CALENDAR) */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto max-h-[750px] relative scrollbar-thin"
        style={{ height: "calc(100vh - 280px)", minHeight: "560px" }}
      >
        <div
          className="flex relative"
          style={{ height: `${24 * HOUR_HEIGHT}px` }}
        >
          {/* Coluna de Horários (Gutter à Esquerda com 8 AM, 9 AM, etc.) */}
          <div className="w-16 shrink-0 border-r border-border/60 bg-muted/5 select-none">
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="relative text-right pr-2 text-[10px] font-mono text-muted-foreground/70"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="absolute -top-2.5 right-2">
                  {formatGutterHour(hour)}
                </span>
              </div>
            ))}
          </div>

          {/* Colunas dos 7 Dias com as Linhas Horárias e Eventos */}
          <div className="flex-1 grid grid-cols-7 divide-x divide-border/60 relative">
            {daysLayoutData.map(({ day, isDayToday, timedEvents }) => {
              const dayIso = day.toISOString();

              return (
                <div
                  key={dayIso}
                  className={cn(
                    "relative h-full transition-colors overflow-hidden",
                    isDayToday && "bg-primary/[0.02]",
                  )}
                >
                  {/* Linhas Horárias Horizontais */}
                  {HOURS.map((hour) => {
                    const isDragOver =
                      dragOverCell?.dayIso === dayIso &&
                      dragOverCell?.hour === hour;

                    return (
                      <div
                        key={hour}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          setDragOverCell({ dayIso, hour });
                        }}
                        onDragLeave={() => {
                          setDragOverCell(null);
                        }}
                        onDrop={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const relY = e.clientY - rect.top;
                          const minute = relY > HOUR_HEIGHT / 2 ? 30 : 0;
                          handleDropOnTimeSlot(day, hour, minute, e);
                        }}
                        onClick={() => handleSlotClick(day, hour, 0)}
                        style={{ height: `${HOUR_HEIGHT}px` }}
                        className={cn(
                          "border-b border-border/40 hover:bg-muted/30 transition-colors cursor-pointer group relative",
                          isDragOver &&
                            "bg-primary/20 ring-1 ring-primary border-primary",
                        )}
                        title={`Clique para agendar em ${format(day, "EEEE", { locale: ptBR })} às ${String(hour).padStart(2, "0")}:00`}
                      >
                        {/* Linha pontilhada de meia hora (30m) */}
                        <div
                          className="absolute inset-x-0 border-b border-border/20 border-dashed pointer-events-none"
                          style={{ top: `${HOUR_HEIGHT / 2}px` }}
                        />

                        {/* Indicador ao passar o mouse */}
                        <div className="hidden group-hover:flex items-center gap-1 text-[9px] text-muted-foreground/60 pl-2 pt-1 font-mono pointer-events-none">
                          <Plus className="w-2.5 h-2.5" />
                          <span>{String(hour).padStart(2, "0")}:00</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* LINHA VERMELHA DE HORA ATUAL (EXATAMENTE COMO NO GOOGLE CALENDAR) */}
                  {isDayToday && (
                    <div
                      className="absolute inset-x-0 z-20 pointer-events-none flex items-center"
                      style={{
                        top: `${(currentTime.getHours() * 60 + currentTime.getMinutes()) * MINUTE_HEIGHT}px`,
                      }}
                    >
                      {/* Bolinha vermelha no início da linha */}
                      <div className="w-2.5 h-2.5 rounded-full bg-[#ea4335] shadow-xs -ml-1.5 shrink-0 ring-2 ring-background animate-pulse" />
                      {/* Linha vermelha estendida */}
                      <div className="flex-1 h-[2px] bg-[#ea4335] shadow-xs" />
                    </div>
                  )}

                  {/* BLOCOS DE EVENTOS POSICIONADOS POR HORÁRIO E DURAÇÃO (ESTILO GOOGLE CALENDAR) */}
                  {timedEvents.map(
                    ({
                      task,
                      start,
                      end,
                      durationMinutes,
                      top,
                      height,
                      column,
                      totalColumns,
                    }) => {
                      const colors = getEventColor(task);
                      const isDone = !!task.done_date;
                      const gcalHtmlLink =
                        task.google_calendar_html_link ||
                        getGCalHtmlLink(task.id);
                      const isGcal = Boolean(
                        task.google_calendar_event_id ||
                          getGCalEventId(task.id),
                      );

                      const timeDisplay = task.data_fim
                        ? formatGoogleCalendarTime(start, end)
                        : format(start, "HH:mm");

                      // Cálculo de sobreposição em cascata (estilo Google Calendar)
                      // Os cartões mantêm largura ampla sem NUNCA ultrapassar a linha da coluna do dia
                      const zIndex = 10 + column;
                      let leftPercent = 2;
                      let widthPercent = 96;

                      if (totalColumns === 1) {
                        leftPercent = 2;
                        widthPercent = 96;
                      } else if (totalColumns === 2) {
                        // 2 eventos sobrepostos:
                        // O primeiro fica à esquerda (78% de largura)
                        // O segundo fica deslocado para a direita (left 18%, 78% de largura -> 18 + 78 = 96%)
                        leftPercent = column === 0 ? 2 : 18;
                        widthPercent = 78;
                      } else {
                        // 3 ou mais eventos sobrepostos:
                        // O offset é calculado proporcionalmente garantindo que left + width nunca exceda 96%
                        const maxOffset = 40;
                        const step = maxOffset / (totalColumns - 1);
                        leftPercent = 2 + column * step;
                        widthPercent = Math.max(
                          50,
                          Math.min(78, 96 - leftPercent),
                        );
                      }

                      return (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData(
                              "application/json",
                              JSON.stringify({
                                taskId: task.id,
                                durationMinutes,
                              }),
                            );
                            e.dataTransfer.setData(
                              "text/plain",
                              String(task.id),
                            );
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditTask(task);
                          }}
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            left: `${leftPercent}%`,
                            width: `${widthPercent}%`,
                            zIndex,
                          }}
                          className={cn(
                            "absolute rounded-md px-2 py-1 border ring-1 ring-background/80 shadow-md transition-all cursor-pointer select-none overflow-hidden flex flex-col justify-start leading-tight",
                            "hover:z-50 hover:shadow-2xl hover:scale-[1.01]",
                            colors.bg,
                            colors.border,
                            colors.text,
                            colors.hover,
                            isDone && "opacity-60 saturate-50 line-through",
                          )}
                          title={`${(task.text || "").replace(/^(Reunião:\s*)+/i, "Reunião: ")} (${timeDisplay})`}
                        >
                          {/* Linha do Título com Ícones */}
                          <div className="flex items-center justify-between gap-1 min-w-0">
                            <span className="font-semibold text-[11px] truncate flex-1 leading-snug">
                              {(task.text || "").replace(
                                /^(Reunião:\s*)+/i,
                                "Reunião: ",
                              )}
                            </span>

                            {/* Ícone de Notas se houver */}
                            {task.notes && (
                              <span
                                className="shrink-0 opacity-80 hover:opacity-100"
                                title={`Notas: ${task.notes}`}
                              >
                                <FileText className="w-2.5 h-2.5" />
                              </span>
                            )}

                            {/* Ícone Google Calendar se conectado */}
                            {isGcal && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (gcalHtmlLink) {
                                    window.open(gcalHtmlLink, "_blank");
                                  }
                                }}
                                className="shrink-0 opacity-75 hover:opacity-100 hover:scale-110 transition-transform"
                                title="Sincronizado com Google Agenda"
                              >
                                <Calendar className="w-2.5 h-2.5" />
                              </span>
                            )}

                            {/* Link de vídeo chamada se houver */}
                            {task.link_chamada && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(task.link_chamada!, "_blank");
                                }}
                                className="shrink-0 opacity-75 hover:opacity-100"
                                title="Abrir chamada"
                              >
                                <Video className="w-2.5 h-2.5" />
                              </span>
                            )}

                            {/* Botão de Excluir Rápido (exclui no CRM e no Google Agenda) */}
                            <span
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (
                                  !window.confirm(
                                    `Deseja excluir "${task.text}"? Ela também será removida do Google Agenda.`,
                                  )
                                ) {
                                  return;
                                }
                                try {
                                  const gcalId =
                                    task.google_calendar_event_id ||
                                    getGCalEventId(task.id);
                                  if (gcalId) {
                                    deleteEventFromGoogleCalendar({
                                      taskId: task.id,
                                      eventId: gcalId,
                                    }).catch(() => {});
                                  }
                                  await dataProvider.delete("tasks", {
                                    id: task.id,
                                    previousData: task,
                                  });
                                  notify(
                                    "Atividade excluída do CRM e do Google Agenda",
                                    { type: "success" },
                                  );
                                  onRefresh();
                                } catch {
                                  notify("Erro ao excluir atividade", {
                                    type: "error",
                                  });
                                }
                              }}
                              className="shrink-0 opacity-0 group-hover:opacity-80 hover:!opacity-100 hover:text-red-300 transition-opacity p-0.5"
                              title="Excluir atividade (CRM e Google Agenda)"
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </span>
                          </div>

                          {/* Horário formato Google Calendar e Proprietário */}
                          <div className="flex items-center justify-between gap-1 text-[10px] font-normal opacity-90 truncate mt-0.5">
                            <span className="truncate">{timeDisplay}</span>
                            {task.sales_id &&
                              salesMap.has(String(task.sales_id)) && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1 py-0.2 rounded bg-black/25 text-[9px] font-semibold shrink-0"
                                  title={`Responsável: ${salesMap.get(String(task.sales_id))}`}
                                >
                                  👤 {salesMap.get(String(task.sales_id))}
                                </span>
                              )}
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
